import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArchiveRestore, RefreshCw, RotateCcw, Search, ShieldAlert, Trash2 } from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDateTime } from '../utils/dateFormat';

const entityLabels = {
  user: 'User',
  customer: 'Customer',
  inventory_stock: 'Inventory Stock',
  product_type: 'Product Type',
  device_oem: 'Device OEM',
  device_name: 'Device Name',
  product: 'Product',
  warehouse: 'Office Location'
};

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch (err) {
    return {};
  }
};

const tableSummary = (summary = {}) => {
  const tableCounts = summary.table_counts || {};
  const parts = [
    summary.deleted_users ? `Users ${summary.deleted_users}` : '',
    summary.deleted_customers ? `Customers ${summary.deleted_customers}` : '',
    summary.deleted_inventory ? `Stock ${summary.deleted_inventory}` : '',
    summary.deleted_sales || summary.deleted_sales === 0 ? `Sales ${summary.deleted_sales}` : '',
    summary.deleted_gps_devices ? `GPS ${summary.deleted_gps_devices}` : '',
    summary.unlinked_sales ? `Unlinked Sales ${summary.unlinked_sales}` : ''
  ].filter(Boolean);

  if (parts.length > 0) return parts.join(' | ');
  const visibleTables = Object.entries(tableCounts).filter(([, count]) => Number(count) > 0).slice(0, 4);
  if (visibleTables.length > 0) {
    return visibleTables.map(([table, count]) => `${table.replace(/_/g, ' ')} ${count}`).join(' | ');
  }
  return `${summary.total_rows || 0} rows`;
};

export default function DeleteHistory({ deleteView = '' }) {
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState('deleted');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const user = useMemo(getCurrentUser, []);
  const isSuperAdmin = user?.role === 'superadmin';

  const fetchDeleteHistory = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      params.set('limit', '300');
      const res = await axios.get(`${API_URL}/api/delete-history?${params.toString()}`, { headers: getHeaders() });
      setRecords(res.data || []);
    } catch (err) {
      alert(err.response?.data?.message || 'Delete history load failed');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, status]);

  useEffect(() => {
    fetchDeleteHistory();
  }, [fetchDeleteHistory]);

  useEffect(() => {
    const statusMap = {
      'delete-history-deleted': 'deleted',
      'delete-history-restored': 'restored',
      'delete-history-all': 'all'
    };
    if (statusMap[deleteView]) setStatus(statusMap[deleteView]);
  }, [deleteView]);

  const restoreRecord = async (record) => {
    if (!record?.restore_ready) return;
    const confirmText = window.prompt(`Type RESTORE to restore ${record.entity_label || record.entity_type}`);
    if (confirmText !== 'RESTORE') return;

    setRestoringId(record.id);
    try {
      const res = await axios.post(`${API_URL}/api/delete-history/${record.id}/restore`, {}, { headers: getHeaders() });
      alert(res.data?.message || 'Record restored successfully');
      fetchDeleteHistory();
    } catch (err) {
      alert(err.response?.data?.message || 'Restore failed');
    } finally {
      setRestoringId(null);
    }
  };

  const permanentlyDeleteRecord = async (record) => {
    const confirmText = window.prompt(`Type PERMANENT DELETE to remove history for ${record.entity_label || record.entity_type}`);
    if (confirmText !== 'PERMANENT DELETE') return;

    setDeletingId(record.id);
    try {
      const res = await axios.delete(`${API_URL}/api/delete-history/${record.id}`, { headers: getHeaders() });
      alert(res.data?.message || 'Delete history permanently removed');
      fetchDeleteHistory();
    } catch (err) {
      alert(err.response?.data?.message || 'Permanent delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((record) => [
      record.entity_type,
      entityLabels[record.entity_type],
      record.entity_id,
      record.entity_label,
      record.deleted_by_name,
      record.status,
      tableSummary(record.summary)
    ].filter(Boolean).join(' ').toLowerCase().includes(term));
  }, [records, search]);

  const stats = [
    { label: 'Deleted', value: records.filter((item) => item.status === 'deleted').length, color: '#dc2626' },
    { label: 'Restored', value: records.filter((item) => item.status === 'restored').length, color: '#047857' },
    { label: 'Shown', value: filteredRecords.length, color: '#1d4ed8' },
    { label: 'Rows Archived', value: records.reduce((total, item) => total + Number(item.summary?.total_rows || 0), 0), color: '#7c3aed' }
  ];

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: '28px', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <ShieldAlert size={20} /> Only Super Admin can open Delete History.
      </div>
    );
  }

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div>
          <h2 style={{ color: '#1e3a5f', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '9px' }}>
            <Trash2 size={22} /> Delete History
          </h2>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Super Admin deleted records and restore status.</p>
        </div>
        <button type="button" onClick={fetchDeleteHistory} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: '#f1f5f9', color: '#0f172a', border: '1px solid #dbe3ef', padding: '9px 14px', borderRadius: '8px', cursor: loading ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
          <RefreshCw size={15} /> {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '18px' }}>
        {stats.map((item) => (
          <div key={item.label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', borderLeft: `4px solid ${item.color}` }}>
            <span style={{ display: 'block', color: '#64748b', fontSize: '12px', fontWeight: 800, marginBottom: '6px' }}>{item.label}</span>
            <strong style={{ color: item.color, fontSize: '24px' }}>{item.value}</strong>
          </div>
        ))}
      </div>
      </div>

      <div className="fixed-panel-body">
      <section style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div style={{ display: 'inline-flex', background: '#f8fafc', border: '1px solid #dbe3ef', borderRadius: '8px', padding: '4px', gap: '4px' }}>
            {[
              { key: 'deleted', label: 'Deleted' },
              { key: 'restored', label: 'Restored' },
              { key: 'all', label: 'All' }
            ].map((item) => (
              <button key={item.key} type="button" onClick={() => setStatus(item.key)} style={{ border: 'none', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', background: status === item.key ? '#0b66ae' : 'transparent', color: status === item.key ? 'white' : '#475569' }}>
                {item.label}
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '260px', background: '#f8fafc', border: '1px solid #dbe3ef', borderRadius: '8px', padding: '8px 10px' }}>
            <Search size={16} color="#64748b" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search deleted records..." style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', color: '#0f172a' }} />
          </label>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '1040px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#334155' }}>
                {['Deleted At', 'Type', 'Record', 'Deleted By', 'Archived Data', 'Status', 'Restored At', 'Action'].map((head) => (
                  <th key={head} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', borderBottom: '1px solid #e5e7eb' }}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>Loading delete history...</td>
                </tr>
              ) : filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>No delete history found.</td>
                </tr>
              ) : filteredRecords.map((record) => (
                <tr key={record.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDateTime(record.deleted_at)}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px', color: '#0f766e', fontWeight: 'bold' }}>{entityLabels[record.entity_type] || record.entity_type}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px', color: '#0f172a', fontWeight: 'bold' }}>
                    {record.entity_label || `#${record.entity_id}`}
                    <span style={{ display: 'block', color: '#64748b', fontSize: '11px', fontWeight: 700 }}>ID: {record.entity_id}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569' }}>{record.deleted_by_name || '-'}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569', maxWidth: '300px' }}>{tableSummary(record.summary)}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: record.status === 'restored' ? '#047857' : '#dc2626', fontWeight: 'bold' }}>{record.status}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDateTime(record.restored_at)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => restoreRecord(record)} disabled={!record.restore_ready || restoringId === record.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: record.restore_ready ? '#ecfdf5' : '#f3f4f6', color: record.restore_ready ? '#047857' : '#9ca3af', border: `1px solid ${record.restore_ready ? '#bbf7d0' : '#e5e7eb'}`, padding: '7px 11px', borderRadius: '8px', cursor: record.restore_ready ? 'pointer' : 'not-allowed', fontSize: '12px', fontWeight: 800 }}>
                      {restoringId === record.id ? <RotateCcw size={14} /> : <ArchiveRestore size={14} />}
                      {restoringId === record.id ? 'Restoring...' : 'Restore'}
                    </button>
                    <button type="button" onClick={() => permanentlyDeleteRecord(record)} disabled={deletingId === record.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: deletingId === record.id ? '#f3f4f6' : '#fef2f2', color: deletingId === record.id ? '#9ca3af' : '#dc2626', border: `1px solid ${deletingId === record.id ? '#e5e7eb' : '#fecaca'}`, padding: '7px 11px', borderRadius: '8px', cursor: deletingId === record.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 800 }}>
                      <Trash2 size={14} />
                      {deletingId === record.id ? 'Deleting...' : 'Permanent'}
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>
    </div>
  );
}
